<script setup>
import { computed } from 'vue';

import { BULK_CV_STATUS_META } from '@/composables/recruitments/useBulkCvBatches';
import useAuth from '@/composables/auth/useAuth';

const props = defineProps({
  item: {
    type: Object,
    required: true
  },
  batch: {
    type: Object,
    default: () => null
  },
  selected: {
    type: Boolean,
    default: false
  },
  loading: {
    type: Boolean,
    default: false
  }
});
const emit = defineEmits(['review', 'toggle-selection']);
const { hasAccess } = useAuth();
const canViewCandidate = computed(
  () =>
    hasAccess({ name: 'candidate', option: 'view_global' }) ||
    hasAccess({ name: 'candidate', option: 'view_own' })
);
const canViewApplicant = computed(() =>
  hasAccess({ name: 'applicant', option: 'view' })
);

const statusMeta = computed(
  () =>
    BULK_CV_STATUS_META[props.item.status] || {
      label: String(props.item.status || 'Unknown').replace(/_/g, ' '),
      variant: 'secondary',
      icon: 'help-circle'
    }
);

const isProcessing = computed(() =>
  [
    'uploading',
    'queued',
    'parsing',
    'candidate_match_check',
    'creating_candidate',
    'updating_candidate',
    'creating_applicant',
    'updating_existing_applicant'
  ].includes(props.item.status)
);

const canSelect = computed(
  () =>
    props.item.status === 'action_required' &&
    props.item.scenario !== 'multiple_matches' &&
    Boolean(props.item.condition_key) &&
    props.item.allowed_actions?.length > 0
);

const parsedName = computed(() => {
  const candidate = props.item.parsed_candidate;
  return [candidate?.first_name, candidate?.middle_name, candidate?.last_name]
    .filter(Boolean)
    .join(' ');
});

const firstMatch = computed(() => props.item.matches?.[0]);
const hasSelectedJob = computed(() => Boolean(props.batch?.job?.id));
const matchSummary = computed(() => {
  if (props.item.scenario === 'multiple_matches') {
    return `${props.item.matches.length} possible candidate matches`;
  }
  if (props.item.scenario === 'existing_applicant') {
    return hasSelectedJob.value
      ? 'Candidate already applied to this job'
      : 'Existing candidate found';
  }
  if (props.item.scenario === 'existing_candidate') {
    return 'Existing candidate found';
  }
  return '';
});
</script>

<template>
  <div
    class="parsing-item w-100 rounded border bg-color-1 dark-bg-color-12 mb-2"
    :class="{ 'selected-item': selected }"
  >
    <div class="d-flex align-items-start p-3">
      <div
        v-if="canSelect"
        class="me-3 pt-1"
      >
        <input
          type="checkbox"
          :checked="selected"
          :aria-label="`Select ${item.name}`"
          @change="emit('toggle-selection', item)"
        />
      </div>

      <div class="flex-1 min-width-0">
        <div class="d-flex flex-wrap justify-content-between gap-2">
          <div class="min-width-0">
            <div class="fw-bold text-14px text-semi-contrast text-truncate">
              {{ item.name }}
            </div>
            <div
              v-if="item.size"
              class="text-11px text-muted"
            >
              {{ item.size }}
            </div>
          </div>

          <span
            class="status-badge badge"
            :class="`badge-outline-${statusMeta.variant}`"
          >
            <FeatherIcon
              :type="isProcessing ? 'loader' : statusMeta.icon"
              size="12"
              :animation="isProcessing ? 'spin' : undefined"
              class="me-1"
            />
            {{ statusMeta.label }}
          </span>
        </div>

        <div
          v-if="parsedName"
          class="text-12px mt-2"
        >
          <span class="text-muted">Parsed candidate:</span>
          <span class="fw-bold ms-1">{{ parsedName }}</span>
          <span
            v-if="item.parsed_candidate?.candidate_email"
            class="ms-2"
          >
            {{ item.parsed_candidate.candidate_email }}
          </span>
        </div>

        <div
          v-if="matchSummary"
          class="match-summary text-12px mt-2 rounded px-2 py-1"
          :class="
            item.scenario === 'existing_applicant'
              ? 'text-warning'
              : 'text-info'
          "
        >
          <FeatherIcon
            type="users"
            size="13"
            class="me-1"
          />
          {{ matchSummary }}
          <template
            v-if="firstMatch?.candidate?.id && item.matches.length === 1"
          >
            · CC-{{ firstMatch.candidate.id }}
          </template>
        </div>

        <div
          v-if="item.error"
          class="error-detail text-danger text-12px mt-2"
        >
          <FeatherIcon
            type="alert-circle"
            size="13"
            class="me-1"
          />
          {{ item.error.message }}
          <span
            v-if="item.error.code"
            class="text-muted ms-1"
          >
            ({{ item.error.code }})
          </span>
        </div>

        <div
          v-if="
            item.status === 'completed' &&
            (item.final_candidate || item.final_applicant)
          "
          class="result-links d-flex flex-wrap align-items-center gap-2 mt-3"
        >
          <RouterLink
            v-if="
              item.final_candidate?.id &&
              item.final_candidate_exists &&
              !item.final_candidate?.restricted &&
              canViewCandidate
            "
            :to="
              item.final_candidate.detail_url || {
                name: 'recruitments-candidates-details',
                params: { id: item.final_candidate.id }
              }
            "
            class="btn btn-sm btn-outline-success"
          >
            <FeatherIcon
              type="user"
              size="13"
              class="me-1"
            />
            Candidate CC-{{ item.final_candidate.id }}
          </RouterLink>
          <span
            v-else-if="item.final_candidate?.id"
            class="badge badge-outline-secondary"
          >
            Candidate CC-{{ item.final_candidate.id }}
            <template v-if="item.final_candidate?.restricted"
              >(restricted)</template
            >
            <template v-else-if="!item.final_candidate_exists"
              >(unavailable)</template
            >
          </span>
          <RouterLink
            v-if="
              item.final_applicant?.id &&
              item.final_applicant_exists &&
              item.final_candidate?.id &&
              item.final_candidate_exists &&
              (!item.final_applicant.detail_url_authoritative ||
                item.final_applicant.detail_url) &&
              canViewApplicant
            "
            :to="
              item.final_applicant.detail_url || {
                name: 'recruitments-applicants',
                query: {
                  candidate_id: item.final_candidate.id,
                  job_id: batch?.job?.id
                }
              }
            "
            class="btn btn-sm btn-outline-info"
          >
            <FeatherIcon
              type="briefcase"
              size="13"
              class="me-1"
            />
            Applicant AP-{{ item.final_applicant.id }}
          </RouterLink>
          <span
            v-else-if="item.final_applicant?.id"
            class="badge badge-outline-secondary"
          >
            <template v-if="item.final_applicant.restricted">
              Applicant record (restricted)
            </template>
            <template v-else
              >Applicant AP-{{ item.final_applicant.id }}</template
            >
            <template
              v-if="
                !item.final_applicant.restricted &&
                item.final_applicant.detail_url_authoritative &&
                !item.final_applicant.detail_url
              "
              >(restricted)</template
            >
            <template v-else-if="!item.final_applicant_exists"
              >(unavailable)</template
            >
          </span>
        </div>
      </div>

      <div class="ms-3 item-actions flex-shrink-0">
        <button
          v-if="item.status === 'action_required'"
          class="btn btn-sm btn-warning"
          :disabled="loading"
          @click="emit('review', item)"
        >
          Parsing Summary
        </button>
      </div>
    </div>
  </div>
</template>

<style lang="scss" scoped>
.parsing-item {
  transition: border-color 0.15s ease, box-shadow 0.15s ease;
}

.selected-item {
  border-color: var(--bs-info) !important;
  box-shadow: inset 3px 0 0 var(--bs-info);
}

.min-width-0 {
  min-width: 0;
}

.status-badge {
  white-space: nowrap;
}

.match-summary {
  background: rgba(33, 150, 243, 0.08);
  width: fit-content;
}

.error-detail {
  max-width: 650px;
}

@media screen and (max-width: 680px) {
  .item-actions {
    margin-left: 0 !important;
    margin-top: 10px;
    width: 100%;
  }

  .parsing-item > div {
    flex-wrap: wrap;
  }
}
</style>
